/**
 * Parsed representation of a single SSE (Server-Sent Event) message.
 * @template T The expected shape of the JSON payload in the `data:` field.
 */
export interface SseMessage<T = unknown> {
	/** The event type. Defaults to `'message'` when the stream omits the `event:` field. */
	readonly type: string;
	/** Parsed JSON payload from the `data:` field. */
	readonly data: T;
	/** Optional message identifier from the `id:` field. */
	readonly id?: string;
	/** Reconnect delay hint (ms) from the `retry:` field, if the server sent one. */
	readonly retryMs?: number;
}

/** The `event:` name of the once-per-connection handshake frame. */
export const SSE_ACK_EVENT = 'ack';

/**
 * What a subscriber can tell about one stream's connection to the server.
 *
 * `connecting` belongs to a stream nothing is known about yet — its first attempt, which is
 * not news and is reported nowhere. `reconnecting` belongs to a stream that was up, or has
 * failed, and is on its way back: it is the state a retry loop sits in, and it *is* reported,
 * because a customer whose figures have stopped moving should be told why.
 */
export type SseConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/** Every live stream at once; `idle` when nothing is subscribed to anything. */
export type SseStatus = SseConnectionStatus | 'idle';

/**
 * Thrown when the SSE endpoint responds with a non-2xx HTTP status.
 */
export class SseHttpError extends Error {
	constructor(
		public readonly status: number,
		public readonly url: string
	) {
		super(`SSE connection failed with HTTP ${status} at ${url}`);
		this.name = 'SseHttpError';
	}
}

/** Maximum delay between reconnect attempts (milliseconds). */
export const SSE_MAX_RECONNECT_DELAY_MS = 30_000;

/** Base delay for the first reconnect attempt (milliseconds). */
export const SSE_BASE_RECONNECT_DELAY_MS = 1_000;

/**
 * How long a stream may stay down before the bar stops saying `reconnecting` and warns that the
 * figures may be stale instead (milliseconds, measured from the drop). This is the whole retry
 * window: the drop is reported as `reconnecting` from the moment it happens, and only becomes
 * `disconnected` once the window has run out without a handshake. Retrying carries on either
 * way — this only decides when the message stops being reassuring.
 */
export const SSE_OUTAGE_WARNING_MS = 30_000;
